/**
 * useCreateGroup Hook
 *
 * React Query mutation hook for creating a new group.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { groupService } from '../../../api/group.service';
import type { GroupSanitized, GroupCreateDTO } from '../../../types/group.types';
import { getErrorMessage } from '../../../lib/utils/errors';

/**
 * Hook for creating a group
 */
export const useCreateGroup = (
  tenantId: string
): UseMutationResult<GroupSanitized, Error, GroupCreateDTO> => {
  const queryClient = useQueryClient();

  return useMutation<GroupSanitized, Error, GroupCreateDTO>({
    mutationFn: (data) => {
      console.log('[useCreateGroup] Creating group:', data.groupName);
      return groupService.createGroup(tenantId, data);
    },
    onSuccess: (group) => {
      console.log('[useCreateGroup] Group created successfully:', group.groupId);
      // Invalidate groups list to refresh the data
      queryClient.invalidateQueries({ queryKey: ['groups', tenantId] });
      // If users were added, invalidate users list too
      if (group.users && group.users.length > 0) {
        queryClient.invalidateQueries({ queryKey: ['users', tenantId] });
      }
    },
    onError: (error) => {
      console.error('[useCreateGroup] Failed to create group:', getErrorMessage(error));
    },
  });
};
